import type { JSX } from 'react';
import { Link } from '@tanstack/react-router';
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';

/**
 * Barre du haut de l'AppShell : chaque page y déclare son fil d'Ariane et ses
 * actions avec `<PageChrome crumbs={...} actions={...} />`, rendus par portail
 * dans la barre (le contexte React de la page est conservé : état local,
 * mutations, formulaires). Sans `<PageChrome>`, la barre affiche
 * « Majuscule › <entrée de navigation active> ».
 */

/** Chemins sans paramètre, cibles possibles d'un élément de fil d'Ariane. */
export type AppPath =
  | '/'
  | '/clients'
  | '/requests'
  | '/pipeline'
  | '/companies'
  | '/todo'
  | '/catalog'
  | '/use-cases'
  | '/billing'
  | '/webhooks'
  | '/alerts'
  | '/settings'
  | '/team'
  | '/appointment-types';

export interface Crumb {
  label: ReactNode;
  /** Lien vers une page parente ; omis pour la page courante (dernier élément). */
  to?: AppPath;
  /** Libellé en mono (référence technique : `req_…`). */
  mono?: boolean;
}

interface TopbarContextValue {
  crumbsTarget: HTMLElement | null;
  actionsTarget: HTMLElement | null;
  /** Enregistre un `<PageChrome crumbs>` monté (le fil par défaut s'efface) ; renvoie le désenregistrement. */
  registerCrumbs: () => () => void;
}

const TopbarContext = createContext<TopbarContextValue | null>(null);

/** Fil d'Ariane rendu dans la barre : « Majuscule › … », dernier élément en gras. */
export function TopbarCrumbs({ crumbs }: { crumbs: readonly Crumb[] }): JSX.Element {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>Majuscule</BreadcrumbItem>
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <Fragment key={index}>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {last || crumb.to === undefined ? (
                  <BreadcrumbPage className={cn(crumb.mono === true && 'font-mono')}>
                    {crumb.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.to} className={cn(crumb.mono === true && 'font-mono')}>
                      {crumb.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

interface TopbarProps {
  /** Fil affiché quand aucune page n'en déclare (entrée de navigation active). */
  fallbackCrumbs: readonly Crumb[];
}

/* Cibles des portails (lues par PageChrome) et état d'enregistrement (lu par Topbar). */
interface TopbarState {
  setCrumbsTarget: (element: HTMLElement | null) => void;
  setActionsTarget: (element: HTMLElement | null) => void;
  hasPageCrumbs: boolean;
}

const TopbarStateContext = createContext<TopbarState | null>(null);

/** Barre du haut (48px, filet en bas) : fil d'Ariane à gauche, actions de page à droite. */
export function Topbar({ fallbackCrumbs }: TopbarProps): JSX.Element {
  const state = useContext(TopbarStateContext);
  if (state === null) {
    throw new Error('Topbar doit être rendu dans un TopbarProvider');
  }
  return (
    <header className="sticky top-0 z-20 flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border bg-background px-6 py-2">
      <div className="min-w-0">
        {state.hasPageCrumbs ? null : <TopbarCrumbs crumbs={fallbackCrumbs} />}
        <div ref={state.setCrumbsTarget} className="contents" />
      </div>
      <div ref={state.setActionsTarget} className="flex flex-wrap items-center gap-2" />
    </header>
  );
}

/** Fournit les emplacements de la barre du haut (posé par l'AppShell, `layout.tsx`). */
export function TopbarProvider({ children }: { children: ReactNode }): JSX.Element {
  const [crumbsTarget, setCrumbsTarget] = useState<HTMLElement | null>(null);
  const [actionsTarget, setActionsTarget] = useState<HTMLElement | null>(null);
  const [crumbsCount, setCrumbsCount] = useState(0);

  const registerCrumbs = useCallback((): (() => void) => {
    setCrumbsCount((count) => count + 1);
    return () => {
      setCrumbsCount((count) => count - 1);
    };
  }, []);

  const value = useMemo<TopbarContextValue>(
    () => ({ crumbsTarget, actionsTarget, registerCrumbs }),
    [crumbsTarget, actionsTarget, registerCrumbs],
  );
  const state = useMemo<TopbarState>(
    () => ({ setCrumbsTarget, setActionsTarget, hasPageCrumbs: crumbsCount > 0 }),
    [crumbsCount],
  );

  return (
    <TopbarStateContext.Provider value={state}>
      <TopbarContext.Provider value={value}>{children}</TopbarContext.Provider>
    </TopbarStateContext.Provider>
  );
}

interface PageChromeProps {
  /**
   * Fil d'Ariane après « Majuscule » : parents avec `to`, page courante en
   * dernier (sans `to`). Ex. `[{ label: 'Requêtes', to: '/requests' }, { label: id, mono: true }]`.
   */
  crumbs?: readonly Crumb[];
  /**
   * Actions de la page dans la barre du haut, à droite : au plus une action
   * principale (`Button` default), sélecteur de mois (`MonthSwitcher`),
   * actions secondaires en `outline`.
   */
  actions?: ReactNode;
}

/**
 * Déclare le fil d'Ariane et les actions de la page dans la barre du haut.
 * Ne rend rien à sa place : le placer n'importe où dans la page (en général
 * en tête, à côté de `<PageHead>`).
 */
export function PageChrome({ crumbs, actions }: PageChromeProps): JSX.Element | null {
  const context = useContext(TopbarContext);
  const register = context?.registerCrumbs;
  const hasCrumbs = crumbs !== undefined;
  useEffect(() => {
    if (!hasCrumbs || register === undefined) {
      return;
    }
    return register();
  }, [hasCrumbs, register]);

  if (context === null) {
    return null;
  }
  return (
    <>
      {crumbs !== undefined && context.crumbsTarget !== null
        ? createPortal(<TopbarCrumbs crumbs={crumbs} />, context.crumbsTarget)
        : null}
      {actions !== undefined && actions !== null && context.actionsTarget !== null
        ? createPortal(actions, context.actionsTarget)
        : null}
    </>
  );
}
